import { ElementType, useState, useEffect, useCallback } from "react";
import {
	CommonPropertyProps,
	ElementSchema,
	ElementTypeList,
	PayloadRules,
	Search,
	SearchRules,
} from "schemas/elements";
import elementsSchema from "schemas";
import { v4 as uuidv4 } from "uuid";
import {
	AlertProps,
	Elem,
	ElemBefore,
	SearchSelectOptionProps,
} from "@alm/flow-ds/components";
import { FormEntity, IdData } from "hooks/FormDesigner/useElementList";
import { SearchMap } from "../properties";
import { useTranslation } from "react-i18next";
import api from "lib/api";
import Entity from "../Entity";
import Payload from "../Payload";
import { status } from "../../../../constants";
import axios from "axios";

type searchProps =
	| "label"
	| "columns"
	| "view"
	| "payload"
	| "overwriteDataWith";

type searchPropertyTypes = "idSelect" | "columns" | "elements" | "payload";

type Props = Omit<CommonPropertyProps, "value"> & {
	elem: string;
	value: Search;
	addAlert: (alert: string, status: AlertProps["status"]) => void;
	formEntity: FormEntity;
	showSearch?: boolean;
};

export default ({
	updateStore,
	field,
	required,
	value = {
		columns: [{ id: null, label: "" }],
		payload: { filter: [], sort: [], "page-size": 10 },
	},
	desc,
	validation,
	elementIndex,
	elem,
	addAlert,
	formEntity,
	isEmu,
	showSearch,
}: Props) => {
	const { t } = useTranslation();
	const props = {
		field,
		required,
		elementIndex,
		validation,
		value: { ...value },
		desc: desc,
		elem: elem,
	};
	const myElementSchema: ElementSchema = elementsSchema as ElementSchema;
	// looks for the element in the schema to get the props it needs for later
	const searchElement: SearchRules =
		myElementSchema[elem as ElementTypeList].search.rules;

	// looks for the element in the schema to get the props it needs for later
	const payloadElement: PayloadRules =
		myElementSchema[elem as ElementTypeList].search.rules.payload.rules;

	// State passed to select and sort
	const [loading, setLoading] = useState(false);
	const [idList, setIdList] = useState<IdData | undefined>(undefined);
	const [requestUri, setRequestUri] = useState<
		SearchSelectOptionProps | undefined
	>(undefined);

	// Request to get fields list using request-uri
	const getFieldsList = useCallback(async () => {
		const entity = value.payload["request-uri"]?.value.substr(
			value.payload["request-uri"]?.value.lastIndexOf("/") + 1
		);

		try {
			setLoading(true);
			// Check whether request-uri has changed to prevent another request
			if (
				value.payload["request-uri"] &&
				requestUri !== value.payload["request-uri"]
			) {
				const res = await api.entity.getEntityData(entity as string);
				setIdList(res);
				setRequestUri(value.payload["request-uri"]);
			} else if (!entity) {
				setIdList(undefined);
				setRequestUri(undefined);
			}
		} catch (error) {
			addAlert("errorLoadingFormList", status.ERROR);
			if (error && axios.isAxiosError(error))
				console.error(`Error: ${error.message}`);
		}
		setLoading(false);
	}, [requestUri, addAlert, value.payload]);

	useEffect(() => {
		getFieldsList();
	}, [getFieldsList, value.payload]);

	if (showSearch)
		return (
			<Elem className="elem-payload">
				<ElemBefore
					label={t(`formDesigner.properties.${field}`)}
					required={required}
				/>
				<div className="hint-text">
					{t(`formDesigner.elements.${desc}`)}
				</div>
				<div className={`elem-payload-properties`}>
					<Entity
						key={"request-uri"}
						updateStore={updateStore}
						field={"payload"}
						required={
							payloadElement["request-uri"]?.required ===
							undefined
								? false
								: payloadElement["request-uri"]?.required
						}
						elementIndex={elementIndex}
						validation={validation}
						value={{ ...value.payload }}
						nestedField={"search"}
						id={uuidv4()}
						nextProp={"request-uri"}
						desc={payloadElement["request-uri"]?.desc}
						addAlert={addAlert}
					/>
					{Object.keys(searchElement).map(property => {
						const theProperty = property as searchProps;
						if (theProperty !== "payload") {
							const theObject = searchElement[theProperty];
							const Property: ElementType =
								SearchMap[
									theObject.type as searchPropertyTypes
								];
							const id: string = uuidv4();

							return (
								<Property
									key={theProperty}
									{...props}
									updateStore={updateStore}
									value={
										value === undefined
											? undefined
											: value[theProperty]
									}
									entityUri={
										theProperty === "columns" ||
										theProperty === "label" ||
										theProperty === "overwriteDataWith"
											? value.payload["request-uri"]
											: undefined
									}
									loading={loading}
									idList={
										theProperty === "label" ||
										theProperty === "columns" ||
										theProperty === "overwriteDataWith"
											? idList
											: undefined
									}
									disabledPlaceholder={t(
										"formDesigner.placeholders.requestUriRelation"
									)}
									id={id}
									field={theProperty}
									nestedField={"search"}
									desc={theObject?.desc}
									required={theObject?.required}
									addAlert={addAlert}
									formEntity={
										theProperty === "view"
											? formEntity
											: undefined
									}
									isEmu={
										theProperty === "view"
											? isEmu
											: undefined
									}
									searchUri={
										theProperty === "view"
											? value.payload["request-uri"]
											: undefined
									}
								/>
							);
						}
						return false;
					})}
					<Payload
						updateStore={updateStore}
						field={"payload"}
						required={searchElement["payload"]?.required}
						elementIndex={elementIndex}
						validation={validation}
						value={{ ...value.payload }}
						nestedField="search"
						addAlert={addAlert}
						idList={idList}
						disabledPlaceholder={t(
							"formDesigner.placeholders.requestUriRelation"
						)}
						id={uuidv4()}
						loading={loading}
						desc={searchElement["payload"].desc}
						elem={elem}
					/>
				</div>
			</Elem>
		);
	else return <></>;
};
